// client/src/components/watchdog/WatchdogActivityLog.jsx
import React from "react";
import { formatEasternTime, getEasternTimeZoneLabel } from "../../utils/timezone";

const STATUS_ICONS = {
    event_received: "\uD83D\uDCE5",
    queued: "\u23F3",
    validating: "\uD83D\uDD0D",
    validated_ok: "\u2705",
    validated_mismatch: "\uD83D\uDEA8",
    action_pending: "\u26A0\uFE0F",
    action_taken: "\u26A1",
    action_failed: "\u274C",
    dismissed: "\uD83D\uDEAB",
    resolved_by_shiphero: "\uD83D\uDD04",
    fulfilled: "\uD83D\uDCE6",
    cancelled_in_shopify: "\uD83D\uDDD1\uFE0F",
};

const STATUS_LABELS = {
    event_received: "Event received",
    queued: "Queued for validation",
    validating: "Validating...",
    validated_ok: "Validated OK",
    validated_mismatch: "MISMATCH detected",
    action_pending: "Action pending",
    action_taken: "Action taken",
    action_failed: "Action failed",
    dismissed: "Dismissed",
    resolved_by_shiphero: "Resolved by ShipHero",
    fulfilled: "Fulfilled",
    cancelled_in_shopify: "Cancelled in Shopify",
};

const ACTIVITY_FILTERS = [
    { key: "all", label: "All" },
    { key: "errors", label: "Errors" },
    { key: "mismatches", label: "Mismatches" },
    { key: "actions", label: "Actions" },
];

const s = {
    container: {
        background: "#1a1f2e",
        borderRadius: "12px",
        border: "1px solid #1e293b",
        overflow: "hidden",
    },
    header: {
        padding: "14px 20px",
        borderBottom: "1px solid #1e293b",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    title: {
        color: "#e2e8f0",
        fontSize: "14px",
        fontWeight: 700,
    },
    filterRow: {
        display: "flex",
        gap: "6px",
        alignItems: "center",
    },
    filterBtn: (active) => ({
        background: active ? "#6366f1" : "transparent",
        color: active ? "#fff" : "#64748b",
        border: active ? "none" : "1px solid #334155",
        borderRadius: "5px",
        padding: "3px 10px",
        fontSize: "10px",
        fontWeight: 600,
        cursor: "pointer",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
    }),
    freshness: {
        color: "#4a5568",
        fontSize: "10px",
        marginLeft: "8px",
        fontVariantNumeric: "tabular-nums",
    },
    list: {
        maxHeight: "360px",
        overflowY: "auto",
    },
    item: (isMismatch) => ({
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "8px 20px",
        borderBottom: "1px solid #1e293b22",
        background: isMismatch ? "#ef444408" : "transparent",
    }),
    time: {
        color: "#64748b",
        fontSize: "11px",
        fontFamily: "monospace",
        whiteSpace: "nowrap",
        minWidth: "60px",
        paddingTop: "1px",
    },
    icon: { fontSize: "14px" },
    orderNum: {
        color: "#e2e8f0",
        fontWeight: 600,
        fontSize: "13px",
        minWidth: "100px",
    },
    msgCol: {
        flex: 1,
    },
    message: (color) => ({
        color: color,
        fontSize: "12px",
    }),
    detail: {
        fontSize: "11px",
        color: "#64748b",
        marginTop: "2px",
    },
    errorDetail: {
        fontSize: "11px",
        color: "#fca5a5",
        marginTop: "2px",
    },
    empty: {
        padding: "30px",
        textAlign: "center",
        color: "#64748b",
        fontSize: "13px",
    },
};

function formatTime(dateStr) {
    return formatEasternTime(dateStr, { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function statusColor(status) {
    if (status === "validated_mismatch" || status === "action_failed") return "#ef4444";
    if (status === "action_taken") return "#22c55e";
    if (status === "validated_ok") return "#10b981";
    if (status === "resolved_by_shiphero") return "#06b6d4";
    return "#94a3b8";
}

function isMismatchStatus(status) {
    return ["validated_mismatch", "action_taken", "action_failed"].includes(status);
}

export default function WatchdogActivityLog({ activity, loading, lastUpdated, filter, onFilterChange }) {
    if (loading && (!activity || activity.length === 0)) {
        return (
            <div style={s.container}>
                <div style={s.header}>
                    <span style={s.title}>Recent Activity</span>
                </div>
                <div style={s.empty}>Loading...</div>
            </div>
        );
    }

    return (
        <div style={s.container}>
            <div style={s.header}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={s.title}>Recent Activity</span>
                    {lastUpdated && (
                        <span style={s.freshness}>
                            \u00b7 last poll {formatTime(lastUpdated)} {getEasternTimeZoneLabel()}
                        </span>
                    )}
                </div>
                <div style={s.filterRow}>
                    {ACTIVITY_FILTERS.map(f => (
                        <button
                            key={f.key}
                            style={s.filterBtn(filter === f.key)}
                            onClick={() => onFilterChange?.(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>
            <div style={s.list}>
                {(!activity || activity.length === 0) ? (
                    <div style={s.empty}>
                        {filter !== "all"
                            ? `No ${filter} activity found.`
                            : "No activity yet. Events will appear here as webhooks are processed."
                        }
                    </div>
                ) : (
                    activity.map((item, i) => {
                        const details = [];

                        // Cancel/Tag/History breakdown
                        if (item.cancel_status) details.push(`Cancel: ${item.cancel_status}`);
                        if (item.tag_status) details.push(`Tag: ${item.tag_status}`);
                        if (item.history_status) details.push(`History: ${item.history_status}`);
                        if (item.retry_count > 0) details.push(`Retries: ${item.retry_count}`);

                        return (
                            <div key={item.id || i} style={s.item(isMismatchStatus(item.status))}>
                                <span style={s.time}>{formatTime(item.updated_at)}</span>
                                <span style={s.icon}>{STATUS_ICONS[item.status] || "\u2022"}</span>
                                <span style={s.orderNum}>{item.order_number}</span>
                                <div style={s.msgCol}>
                                    <span style={s.message(statusColor(item.status))}>
                                        {STATUS_LABELS[item.status] || item.status}
                                        {item.cancel_status === "success" && item.action_mode === "auto" && " (auto)"}
                                        {item.cancel_status === "success" && item.action_mode === "manual" && " (manual)"}
                                        {item.shiphero_self_cancelled && " \u2014 ShipHero handled it"}
                                    </span>
                                    {details.length > 0 && (
                                        <div style={s.detail}>{details.join(" \u00b7 ")}</div>
                                    )}
                                    {item.validation_error && (
                                        <div style={s.errorDetail}>Validation: {item.validation_error}</div>
                                    )}
                                    {item.action_error && (
                                        <div style={s.errorDetail}>Action: {item.action_error}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
